import { useTranslation } from 'react-i18next';
import { ArrowRightIcon, FileInvoiceLightIcon, MoneyBillWaveLightIcon, UserPenLightIcon } from '@c2fo/liquidity/icons';
import earlyPayInfo from '@/enterprise/assets/instantpayinfo1.svg';
import earlyPayInfoMobile from '@/enterprise/assets/instantpayinfo1mobile.svg';
import instantPayInfo from '@/enterprise/assets/instantpayinfo2.svg';
import instantPayInfoMobile from '@/enterprise/assets/instantpayinfo2mobile.svg';

const HowItWorksCard = ({
  Icon,
  title,
  description,
}: {
  Icon: typeof UserPenLightIcon;
  title: string;
  description: string;
}) => (
  <div className="flex flex-1 flex-col items-center gap-2 rounded-xl border border-stroke bg-canvas p-6 text-center">
    <Icon className="size-9 fill-secondary-500" />
    <span className="text-lg font-bold text-text-primary">{title}</span>
    <span className="text-text-secondary">{description}</span>
  </div>
);

const InstantPayHowItWorks = () => {
  const { t } = useTranslation();

  return (
    <div className="space-y-9">
      <div className="font-serif text-4xl text-text-primary">{t('instantPay.onboarding.howItWorks.title')}</div>

      <div className="flex flex-col gap-4">
        <span className="text-2xl font-bold text-text-primary">
          {t('instantPay.onboarding.howItWorks.process.title')}
        </span>

        <div className="flex flex-col gap-4 rounded-xl bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-6 md:flex-row md:items-stretch">
            <HowItWorksCard
              Icon={UserPenLightIcon}
              title={t('instantPay.onboarding.howItWorks.request.title')}
              description={t('instantPay.onboarding.howItWorks.request.description')}
            />
            <div className="hidden items-center justify-center md:flex">
              <ArrowRightIcon className="size-5 fill-text-secondary" />
            </div>
            <HowItWorksCard
              Icon={MoneyBillWaveLightIcon}
              title={t('instantPay.onboarding.howItWorks.receive.title')}
              description={t('instantPay.onboarding.howItWorks.receive.description')}
            />
            <div className="hidden items-center justify-center md:flex">
              <ArrowRightIcon className="size-5 fill-text-secondary" />
            </div>
            <HowItWorksCard
              Icon={FileInvoiceLightIcon}
              title={t('instantPay.onboarding.howItWorks.history.title')}
              description={t('instantPay.onboarding.howItWorks.history.description')}
            />
          </div>
          <p className="text-center text-xs italic text-text-secondary">
            {t('instantPay.onboarding.howItWorks.process.disclaimer')}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <span className="text-2xl font-bold text-text-primary">
          {t('instantPay.onboarding.howItWorks.paymentFlow.title')}
        </span>

        <div className="flex flex-col gap-8 rounded-xl bg-white p-6 shadow-sm">
          <div className="flex justify-center">
            <p className="text-center text-lg">{t('instantPay.onboarding.howItWorks.paymentFlow.description')}</p>
          </div>

          <div className="flex flex-col gap-4">
            <div className="space-y-1">
              <p className="text-xl font-bold text-text-primary">
                {t('instantPay.onboarding.howItWorks.paymentFlow.earlyPay.title')}
              </p>
              <p className="text-base text-text-secondary">
                {t('instantPay.onboarding.howItWorks.paymentFlow.earlyPay.description')}
              </p>
            </div>
            <img
              src={earlyPayInfoMobile}
              alt={t('instantPay.onboarding.howItWorks.paymentFlow.earlyPay.diagramAlt')}
              className="mx-auto h-auto w-full max-w-[384px] md:hidden"
            />
            <img
              src={earlyPayInfo}
              alt={t('instantPay.onboarding.howItWorks.paymentFlow.earlyPay.diagramAlt')}
              className="hidden h-auto w-full md:block"
            />
          </div>

          <div className="flex flex-col gap-4">
            <div className="space-y-1">
              <p className="text-xl font-bold text-text-primary">
                {t('instantPay.onboarding.howItWorks.paymentFlow.instantPay.title')}
              </p>
              <p className="text-base text-text-secondary">
                {t('instantPay.onboarding.howItWorks.paymentFlow.instantPay.description')}
              </p>
            </div>
            <img
              src={instantPayInfoMobile}
              alt={t('instantPay.onboarding.howItWorks.paymentFlow.instantPay.diagramAlt')}
              className="mx-auto h-auto w-full max-w-[384px] md:hidden"
            />
            <img
              src={instantPayInfo}
              alt={t('instantPay.onboarding.howItWorks.paymentFlow.instantPay.diagramAlt')}
              className="hidden h-auto w-full md:block"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstantPayHowItWorks;
